/**
 * Structured-first facade window/door overlay (issue #123, roadmap batch 5).
 *
 * The enrichment pipeline can commit a per-building `facades[]` block: for the
 * ONE photographed facade, the outward-normal compass `bearing_deg`, a
 * `window_form`, and normalized `{u,v,w,h}` window/door rects. This module lets
 * the #116 overlay read that structured layout FIRST: it matches a footprint
 * wall to a facade entry by bearing (within a documented tolerance), then places
 * the observed openings at their real coordinates and renders the observed
 * window form (lancet = tall pointed, bay = shallow protruding volume, everything
 * else = today's rectangles). When no facade matches a wall, buildings.ts keeps
 * the deterministic `window_count`/`door_color` grid — so absence of facades[] is
 * byte-identical to today (hard constraint #7: report, never fabricate).
 *
 * Everything here is pure and three.js-free (mirrors roofLobes / terraceSegments
 * / buildingParts) and rides the single shared vertex-coloured overlay material
 * in buildings.ts, so the material budget (#103/#116/#17) is untouched — zero new
 * materials or textures. Nothing here reaches raypath/ or any LOS result (hard
 * constraint #1, guarded by buildings.guard.test.ts).
 */
import { bearingDegFromForward, type LocalXZ } from '../core/coordinates';
import {
  doorSlot,
  parseHexColor,
  GLAZING_RGB,
  FRAME_RGB,
  FRAME_MARGIN_M,
  SILL_HEIGHT_M,
  SILL_OVERHANG_M,
  type RgbColor,
} from './facadeTexture';

/** A normalized opening rect within the facade rectangle: u,v in [0,1] from the
 * bottom-left, w,h fractional. */
export interface FacadeRect {
  u: number;
  v: number;
  w: number;
  h: number;
}

/** A door rect additionally carries a validated hex colour (null = fall back to
 * the flat door tint for this door). */
export interface FacadeDoorRect extends FacadeRect {
  color: string | null;
}

/** One parsed, defensively-clamped facade ready to render. */
export interface ParsedFacade {
  /** outward-normal compass bearing of the observed wall, [0,360) */
  bearingDeg: number;
  /** observed dominant window shape, or null (renders as the default rectangle) */
  windowForm: string | null;
  windows: FacadeRect[];
  doors: FacadeDoorRect[];
  confidence: number;
}

export interface FacadeOverlayGeometry {
  /** triangle-list positions, 3 floats/vertex, building-local (base y=0 .. eaves) */
  positions: Float32Array;
  /** matching per-vertex colour, 3 floats/vertex in 0..1 (for a vertexColors material) */
  colors: Float32Array;
}

/**
 * Bearing tolerance (degrees) for matching a footprint wall to a facades[]
 * entry. The writer derives a facade's bearing from the exact same primary edge
 * the viewer measures, so a genuine match sits at ~0 delta; the tolerance only
 * absorbs footprint skew and floating-point drift. Kept below 45 so a
 * rectangle's four walls (90 degrees apart) never both fall inside tolerance —
 * a facade can only ever match one wall.
 */
export const FACADE_BEARING_TOLERANCE_DEG = 30;

/** Shallow depth (m) a `bay` window projects out beyond the wall plane. */
export const BAY_DEPTH_M = 0.35;

// Outward offsets (m) so the overlay layers cleanly, matching the #116 grid
// overlay's proud offsets so observed and grid overlays sit at the same depth.
const OVERLAY_PROUD_BACKING = 0.03;
const OVERLAY_PROUD_FRAME = 0.05;
const OVERLAY_PROUD_GLAZING = 0.07;
const OVERLAY_PROUD_DOOR = 0.06;
/** Sill sits proud of the glazing plane — a real sill projects further from
 * the wall than the glass behind it. */
const OVERLAY_PROUD_SILL = 0.08;
/** Frame member width (m), shared with the canvas-baked texture (issue #149:
 * facadeTexture.ts's FRAME_MARGIN_M) so a wall's texture-drawn windows and its
 * vertex-colour overlay windows never read as two different window styles. */
export const OVERLAY_FRAME_EXPAND = FRAME_MARGIN_M;
/** On a small opening (e.g. a narrow terrace bay window) a fixed frame width
 * can exceed the opening itself and swallow the glazing entirely. Never let
 * the frame consume more than this fraction of either axis of the opening
 * (issue #149 predictable-failure-case b). */
const FRAME_EXPAND_MAX_FRACTION = 0.35;
/** Fraction of a lancet window's height that is the rectangular body; the
 * remainder is the pointed arch to the apex. */
const LANCET_BODY_FRAC = 0.6;

/**
 * Frame member half-width for an opening of size (ww, hh): OVERLAY_FRAME_EXPAND,
 * clamped so a small opening keeps a visible glazing pane rather than being
 * swallowed by the frame (issue #149 predictable-failure-case b).
 */
export function frameExpand(ww: number, hh: number): number {
  return Math.min(OVERLAY_FRAME_EXPAND, ww * FRAME_EXPAND_MAX_FRACTION, hh * FRAME_EXPAND_MAX_FRACTION);
}

/**
 * Adds a sill quad just below a window's frame band, a little wider than it
 * (issue #149 — the sill/frame combo is the dominant close-range cue in
 * reference photos of this area). `fx0`/`fx1`/`fy0` are the frame's already-
 * clamped bounds (the frame's bottom edge is the sill's top edge). No-op when
 * the opening sits at the ground line, leaving no room for a sill below it.
 */
export function addSill(pos: number[], col: number[], at: At, len: number, fx0: number, fx1: number, fy0: number): void {
  const overhang = Math.min(SILL_OVERHANG_M, (fx1 - fx0) / 2);
  const sx0 = Math.max(0, fx0 - overhang);
  const sx1 = Math.min(len, fx1 + overhang);
  const sy1 = fy0;
  const sy0 = Math.max(0, fy0 - SILL_HEIGHT_M);
  if (sy1 <= sy0) return;
  pushColoredQuad(pos, col, at(sx0, sy0, OVERLAY_PROUD_SILL), at(sx1, sy0, OVERLAY_PROUD_SILL), at(sx1, sy1, OVERLAY_PROUD_SILL), at(sx0, sy1, OVERLAY_PROUD_SILL), FRAME_RGB);
}

// --- bearing matching ---------------------------------------------------------

/**
 * Outward-normal compass bearing (deg, 0=N, 90=E, clockwise) of a wall edge,
 * oriented AWAY from `interior` (the footprint centroid). Same outward-normal
 * flip as buildPrimaryFacadeDetail, so a wall's bearing here equals the writer's
 * footprint-derived bearing for the same wall. Null for a degenerate edge.
 */
export function wallBearingDeg(edgeA: LocalXZ, edgeB: LocalXZ, interior: LocalXZ): number | null {
  const dx = edgeB.x - edgeA.x;
  const dz = edgeB.z - edgeA.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return null;
  const ax = dx / len;
  const az = dz / len;
  let nx = -az;
  let nz = ax;
  const midX = (edgeA.x + edgeB.x) / 2;
  const midZ = (edgeA.z + edgeB.z) / 2;
  if (nx * (interior.x - midX) + nz * (interior.z - midZ) > 0) {
    nx = -nx;
    nz = -nz;
  }
  return bearingDegFromForward(nx, nz);
}

/** Smallest circular difference between two compass bearings, in [0, 180]. */
export function bearingDelta(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * The facade whose bearing is nearest `wallBearingDeg` AND within `toleranceDeg`,
 * or null if none qualifies. Never snaps to the nearest wall when the nearest
 * facade is beyond tolerance (predictable-failure-cases: that entry is skipped,
 * the wall keeps the grid).
 */
export function matchFacade(
  wallBearingDeg: number,
  facades: ParsedFacade[],
  toleranceDeg: number = FACADE_BEARING_TOLERANCE_DEG,
): ParsedFacade | null {
  let best: ParsedFacade | null = null;
  let bestDelta = Infinity;
  for (const f of facades) {
    const d = bearingDelta(wallBearingDeg, f.bearingDeg);
    if (d <= toleranceDeg + 1e-9 && d < bestDelta) {
      best = f;
      bestDelta = d;
    }
  }
  return best;
}

// --- defensive parsing --------------------------------------------------------

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function finiteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Clamps a raw rect into [0,1] and inside the facade; null if unusable
 * (non-numeric, or collapses to zero area). Defensive against legacy bad data —
 * never throws (hard constraint #7). */
function clampRect(raw: unknown): FacadeRect | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as { u?: unknown; v?: unknown; w?: unknown; h?: unknown };
  if (!finiteNumber(r.u) || !finiteNumber(r.v) || !finiteNumber(r.w) || !finiteNumber(r.h)) return null;
  const u = clamp01(r.u);
  const v = clamp01(r.v);
  const w = Math.min(Math.max(r.w, 0), 1 - u);
  const h = Math.min(Math.max(r.h, 0), 1 - v);
  if (w <= 0 || h <= 0) return null;
  return { u, v, w, h };
}

function clampDoor(raw: unknown): FacadeDoorRect | null {
  const rect = clampRect(raw);
  if (!rect) return null;
  const color = (raw as { color?: unknown }).color;
  return { ...rect, color: typeof color === 'string' && HEX_RE.test(color) ? color : null };
}

/**
 * Parses the committed `appearance.facades` (untrusted `unknown`) into typed,
 * defensively-clamped entries. A non-array, or an entry without a finite
 * bearing, yields nothing for that entry (so a corrupt block degrades to the
 * grid fallback rather than crashing). Bearings are wrapped into [0,360).
 */
export function parseFacades(raw: unknown): ParsedFacade[] {
  if (!Array.isArray(raw)) return [];
  const out: ParsedFacade[] = [];
  for (const entry of raw) {
    if (entry == null || typeof entry !== 'object') continue;
    const e = entry as {
      bearing_deg?: unknown;
      window_form?: unknown;
      windows?: unknown;
      doors?: unknown;
      confidence?: unknown;
    };
    if (!finiteNumber(e.bearing_deg)) continue;
    const windows = Array.isArray(e.windows)
      ? e.windows.map(clampRect).filter((r): r is FacadeRect => r !== null)
      : [];
    const doors = Array.isArray(e.doors)
      ? e.doors.map(clampDoor).filter((r): r is FacadeDoorRect => r !== null)
      : [];
    out.push({
      bearingDeg: ((e.bearing_deg % 360) + 360) % 360,
      windowForm: typeof e.window_form === 'string' ? e.window_form : null,
      windows,
      doors,
      confidence: finiteNumber(e.confidence) ? clamp01(e.confidence) : 0.6,
    });
  }
  return out;
}

// --- geometry -----------------------------------------------------------------

interface Point3 {
  x: number;
  y: number;
  z: number;
}

function pushTri(out: number[], a: Point3, b: Point3, c: Point3): void {
  out.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
}

function pushColor(col: number[], c: RgbColor, verts: number): void {
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;
  for (let k = 0; k < verts; k++) col.push(r, g, b);
}

function pushColoredQuad(pos: number[], col: number[], p0: Point3, p1: Point3, p2: Point3, p3: Point3, c: RgbColor): void {
  pushTri(pos, p0, p1, p2);
  pushTri(pos, p0, p2, p3);
  pushColor(col, c, 6);
}

/**
 * Builds the observed-facade overlay for one wall: an opaque backing quad in the
 * wall base colour, each observed window rendered in its `window_form`, and each
 * observed door at its colour (or, when `doors` is empty, one grid door at the
 * fallback tint — per-field fallback). Pure and deterministic by `seed`; empty
 * for a degenerate edge. Positions are building-local (y 0..eaves), pushed proud
 * along the outward normal (oriented away from `interiorPoint`).
 */
export function buildObservedFacadeDetail(
  edgeA: LocalXZ,
  edgeB: LocalXZ,
  interiorPoint: LocalXZ,
  eavesHeight: number,
  baseColor: RgbColor,
  facade: ParsedFacade,
  fallbackDoorTint: RgbColor,
  seed: number,
): FacadeOverlayGeometry {
  const dx = edgeB.x - edgeA.x;
  const dz = edgeB.z - edgeA.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6 || eavesHeight <= 0) return { positions: new Float32Array(0), colors: new Float32Array(0) };

  const ax = dx / len;
  const az = dz / len;
  let nx = -az;
  let nz = ax;
  const midX = (edgeA.x + edgeB.x) / 2;
  const midZ = (edgeA.z + edgeB.z) / 2;
  if (nx * (interiorPoint.x - midX) + nz * (interiorPoint.z - midZ) > 0) {
    nx = -nx;
    nz = -nz;
  }

  const at = (u: number, v: number, proud: number): Point3 => ({
    x: edgeA.x + ax * u + nx * proud,
    y: v,
    z: edgeA.z + az * u + nz * proud,
  });

  const pos: number[] = [];
  const col: number[] = [];

  // Backing: full facade in the wall base colour, so the observed openings read
  // cleanly over the tiled wall texture behind them (matches the #116 grid).
  pushColoredQuad(
    pos,
    col,
    at(0, 0, OVERLAY_PROUD_BACKING),
    at(len, 0, OVERLAY_PROUD_BACKING),
    at(len, eavesHeight, OVERLAY_PROUD_BACKING),
    at(0, eavesHeight, OVERLAY_PROUD_BACKING),
    baseColor,
  );

  for (const w of facade.windows) {
    const x0 = w.u * len;
    const y0 = w.v * eavesHeight;
    const ww = w.w * len;
    const hh = w.h * eavesHeight;
    renderWindow(pos, col, at, len, eavesHeight, facade.windowForm, x0, y0, ww, hh);
  }

  if (facade.doors.length > 0) {
    for (const d of facade.doors) {
      const x0 = d.u * len;
      const y0 = d.v * eavesHeight;
      const ww = d.w * len;
      const hh = d.h * eavesHeight;
      const tint = (d.color && parseHexColor(d.color)) || fallbackDoorTint;
      pushColoredQuad(
        pos,
        col,
        at(x0, y0, OVERLAY_PROUD_DOOR),
        at(x0 + ww, y0, OVERLAY_PROUD_DOOR),
        at(x0 + ww, y0 + hh, OVERLAY_PROUD_DOOR),
        at(x0, y0 + hh, OVERLAY_PROUD_DOOR),
        tint,
      );
    }
  } else {
    // Facade observed but no door visible: fall back to the flat door slot for
    // this wall only (per-field fallback, predictable-failure-cases table).
    const door = doorSlot(len, seed);
    if (door.x >= 0 && door.x + door.w <= len && door.h <= eavesHeight) {
      pushColoredQuad(
        pos,
        col,
        at(door.x, 0, OVERLAY_PROUD_DOOR),
        at(door.x + door.w, 0, OVERLAY_PROUD_DOOR),
        at(door.x + door.w, door.h, OVERLAY_PROUD_DOOR),
        at(door.x, door.h, OVERLAY_PROUD_DOOR),
        fallbackDoorTint,
      );
    }
  }

  return { positions: new Float32Array(pos), colors: new Float32Array(col) };
}

type At = (u: number, v: number, proud: number) => Point3;

/** Dispatches a window to its form-specific builder (default = rectangle). */
function renderWindow(
  pos: number[],
  col: number[],
  at: At,
  len: number,
  eaves: number,
  form: string | null,
  x0: number,
  y0: number,
  ww: number,
  hh: number,
): void {
  if (form === 'lancet') {
    lancetWindow(pos, col, at, len, eaves, x0, y0, ww, hh);
  } else if (form === 'bay') {
    bayWindow(pos, col, at, len, eaves, x0, y0, ww, hh);
  } else {
    rectWindow(pos, col, at, len, eaves, x0, y0, ww, hh);
  }
}

/** Today's flat window: an expanded frame quad behind a glazing quad, plus a sill. */
function rectWindow(pos: number[], col: number[], at: At, len: number, eaves: number, x0: number, y0: number, ww: number, hh: number): void {
  const expand = frameExpand(ww, hh);
  const fx0 = Math.max(0, x0 - expand);
  const fy0 = Math.max(0, y0 - expand);
  const fx1 = Math.min(len, x0 + ww + expand);
  const fy1 = Math.min(eaves, y0 + hh + expand);
  pushColoredQuad(pos, col, at(fx0, fy0, OVERLAY_PROUD_FRAME), at(fx1, fy0, OVERLAY_PROUD_FRAME), at(fx1, fy1, OVERLAY_PROUD_FRAME), at(fx0, fy1, OVERLAY_PROUD_FRAME), FRAME_RGB);
  pushColoredQuad(pos, col, at(x0, y0, OVERLAY_PROUD_GLAZING), at(x0 + ww, y0, OVERLAY_PROUD_GLAZING), at(x0 + ww, y0 + hh, OVERLAY_PROUD_GLAZING), at(x0, y0 + hh, OVERLAY_PROUD_GLAZING), GLAZING_RGB);
  addSill(pos, col, at, len, fx0, fx1, fy0);
}

/** A lancet: a rectangular body up to LANCET_BODY_FRAC of the height, then a
 * pointed arch to a single apex centred over the window. Frame first, glazing
 * proud of it; the glazing apex sits exactly at the top of the window rect. */
function lancetWindow(pos: number[], col: number[], at: At, len: number, eaves: number, x0: number, y0: number, ww: number, hh: number): void {
  const cx = x0 + ww / 2;
  const shoulder = y0 + hh * LANCET_BODY_FRAC;
  const apexY = y0 + hh;

  // Frame (expanded, behind the glazing).
  const expand = frameExpand(ww, hh);
  const fx0 = Math.max(0, x0 - expand);
  const fx1 = Math.min(len, x0 + ww + expand);
  const fy0 = Math.max(0, y0 - expand);
  const fShoulder = Math.min(eaves, shoulder);
  const fApex = Math.min(eaves, apexY + expand);
  pushColoredQuad(pos, col, at(fx0, fy0, OVERLAY_PROUD_FRAME), at(fx1, fy0, OVERLAY_PROUD_FRAME), at(fx1, fShoulder, OVERLAY_PROUD_FRAME), at(fx0, fShoulder, OVERLAY_PROUD_FRAME), FRAME_RGB);
  pushTri(pos, at(fx0, fShoulder, OVERLAY_PROUD_FRAME), at(fx1, fShoulder, OVERLAY_PROUD_FRAME), at(cx, fApex, OVERLAY_PROUD_FRAME));
  pushColor(col, FRAME_RGB, 3);
  addSill(pos, col, at, len, fx0, fx1, fy0);

  // Glazing body + pointed arch to the exact window-top apex.
  pushColoredQuad(pos, col, at(x0, y0, OVERLAY_PROUD_GLAZING), at(x0 + ww, y0, OVERLAY_PROUD_GLAZING), at(x0 + ww, shoulder, OVERLAY_PROUD_GLAZING), at(x0, shoulder, OVERLAY_PROUD_GLAZING), GLAZING_RGB);
  pushTri(pos, at(x0, shoulder, OVERLAY_PROUD_GLAZING), at(x0 + ww, shoulder, OVERLAY_PROUD_GLAZING), at(cx, apexY, OVERLAY_PROUD_GLAZING));
  pushColor(col, GLAZING_RGB, 3);
}

/** A bay: an expanded frame around the wall opening, then a glazing box that
 * projects out by BAY_DEPTH_M — a front face plus four short side faces, plus
 * a sill (issue #149: previously the frame ring matched the opening exactly,
 * so the more-proud front face hid it completely from a head-on view). */
function bayWindow(pos: number[], col: number[], at: At, len: number, eaves: number, x0: number, y0: number, ww: number, hh: number): void {
  const inner = OVERLAY_PROUD_GLAZING;
  const outer = OVERLAY_PROUD_GLAZING + BAY_DEPTH_M;
  const expand = frameExpand(ww, hh);
  const fx0 = Math.max(0, x0 - expand);
  const fy0 = Math.max(0, y0 - expand);
  const fx1 = Math.min(len, x0 + ww + expand);
  const fy1 = Math.min(eaves, y0 + hh + expand);
  // Frame ring on the wall plane, expanded beyond the opening so it reads at
  // distance despite sitting behind the bay's more-proud front face.
  pushColoredQuad(pos, col, at(fx0, fy0, OVERLAY_PROUD_FRAME), at(fx1, fy0, OVERLAY_PROUD_FRAME), at(fx1, fy1, OVERLAY_PROUD_FRAME), at(fx0, fy1, OVERLAY_PROUD_FRAME), FRAME_RGB);
  // Front glazing face, pushed out.
  pushColoredQuad(pos, col, at(x0, y0, outer), at(x0 + ww, y0, outer), at(x0 + ww, y0 + hh, outer), at(x0, y0 + hh, outer), GLAZING_RGB);
  // Four side faces connecting the wall-plane opening to the front face.
  pushColoredQuad(pos, col, at(x0, y0, inner), at(x0 + ww, y0, inner), at(x0 + ww, y0, outer), at(x0, y0, outer), GLAZING_RGB); // bottom
  pushColoredQuad(pos, col, at(x0, y0 + hh, inner), at(x0 + ww, y0 + hh, inner), at(x0 + ww, y0 + hh, outer), at(x0, y0 + hh, outer), GLAZING_RGB); // top
  pushColoredQuad(pos, col, at(x0, y0, inner), at(x0, y0 + hh, inner), at(x0, y0 + hh, outer), at(x0, y0, outer), GLAZING_RGB); // left
  pushColoredQuad(pos, col, at(x0 + ww, y0, inner), at(x0 + ww, y0 + hh, inner), at(x0 + ww, y0 + hh, outer), at(x0 + ww, y0, outer), GLAZING_RGB); // right
  addSill(pos, col, at, len, fx0, fx1, fy0);
}
